import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { countWords, isFont, readDocument, usedFontNames } from "@/office/word/document";

export const lektionSchriftartWaehlen: Lesson = {
  id: "schriftart-waehlen",
  title: "Die richtige Schriftart wählen",
  summary: "Woran man Schriftarten unterscheidet und wie man sie in Word zuweist.",
  durationMinutes: 8,
  learningGoals: [
    "Serifen-, serifenlose und nichtproportionale Schriften unterscheiden",
    "Die Schriftart für markierten Text ändern",
    "Schriftarten bewusst und sparsam kombinieren",
  ],
  steps: [
    {
      id: "erster-text",
      title: "Text zum Üben anlegen",
      explanation: [
        "Eine Schriftart bestimmt, wie die Buchstaben deines Textes aussehen. Word " +
          "unterscheidet dabei drei große Gruppen: Serifenschriften wie Times New Roman " +
          "haben kleine Häkchen an den Buchstabenenden, serifenlose Schriften wie Arial " +
          "oder Calibri kommen ohne sie aus, und nichtproportionale Schriften wie " +
          "Courier New geben jedem Zeichen exakt gleich viel Platz.",
        "Alle Werkzeuge dafür findest du im Menüband unter der Registerkarte " +
          "\"Start\" in der Gruppe \"Schriftart\". Damit du sie ausprobieren kannst, " +
          "brauchst du zuerst etwas Text.",
      ],
      keyPoints: [
        "Serifenschriften wirken klassisch, serifenlose modern und klar.",
        "Fahre mit der Maus über eine Schriftart in der Liste – Word zeigt dir sofort eine Vorschau.",
      ],
      task: {
        instruction:
          "Schreibe einen beliebigen Satz mit mindestens fünf Wörtern in dein Word-Dokument.",
        hint: "Zum Beispiel: \"Ich lerne gerade den Umgang mit Schriftarten.\"",
        check: async () => {
          const snapshot = await readDocument();
          const words = countWords(snapshot);

          if (words === 0) {
            return fail(
              "Im Dokument steht noch kein Text.",
              "Klicke in das Word-Dokument und tippe einen Satz. Danach hier erneut auf \"Prüfen\" klicken.",
            );
          }
          if (words < 5) {
            return fail(
              `Dein Text hat erst ${words} ${words === 1 ? "Wort" : "Wörter"}. Es sollen mindestens fünf sein.`,
              "Ergänze den Satz noch um ein paar Wörter.",
            );
          }
          return pass(`Perfekt – dein Text besteht aus ${words} Wörtern.`);
        },
      },
    },
    {
      id: "schriftart-zuweisen",
      title: "Schriftart ändern",
      explanation: [
        "Word ändert die Schriftart immer nur für den Text, der gerade markiert ist. " +
          "Markiere also erst deinen Text – mit der Maus oder mit Strg + A für das " +
          "gesamte Dokument – und wähle dann im Feld \"Schriftart\" den gewünschten Namen aus.",
        "Du kannst den Namen auch direkt in das Feld tippen und mit Enter bestätigen. " +
          "Das ist deutlich schneller, als in der langen Liste zu scrollen.",
      ],
      keyPoints: [
        "Erst markieren, dann formatieren – das gilt in Word für jede Formatierung.",
        "Strg + A markiert das gesamte Dokument.",
      ],
      task: {
        instruction: "Formatiere deinen gesamten Text in der Schriftart \"Arial\".",
        hint: "Strg + A drücken, dann im Feld \"Schriftart\" Arial eintippen und mit Enter bestätigen.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.runs.length === 0) {
            return fail(
              "Im Dokument steht noch kein Text.",
              "Lege zuerst wie im vorherigen Schritt einen Satz an.",
            );
          }

          const wrongFont = snapshot.runs.filter((run) => !isFont(run, "Arial"));
          if (wrongFont.length > 0) {
            const fonts = usedFontNames(snapshot).filter(
              (name) => name.toLowerCase() !== "arial",
            );
            return fail(
              `Noch nicht der ganze Text ist in Arial. Gefunden: ${fonts.join(", ")}.`,
              "Vermutlich war nicht alles markiert. Drücke Strg + A und weise Arial erneut zu.",
            );
          }
          return pass("Sehr gut – dein kompletter Text steht jetzt in Arial.");
        },
      },
    },
    {
      id: "schriftarten-kombinieren",
      title: "Zwei Schriftarten kombinieren",
      explanation: [
        "In der Praxis kombiniert man selten mehr als zwei Schriftarten in einem " +
          "Dokument: eine für Überschriften und eine für den Fließtext. Mehr Schriften " +
          "wirken schnell unruhig und unprofessionell.",
        "Ein bewährtes Paar ist eine serifenlose Schrift für die Überschrift und eine " +
          "Serifenschrift für den Fließtext – oder genau umgekehrt. Wichtig ist nur, " +
          "dass sich die beiden deutlich voneinander unterscheiden.",
      ],
      keyPoints: [
        "Faustregel: maximal zwei Schriftarten pro Dokument.",
        "Überschrift und Fließtext sollten klar unterscheidbar sein.",
      ],
      task: {
        instruction:
          "Schreibe eine zweite Zeile in dein Dokument und formatiere sie in \"Times New Roman\". " +
          "Die erste Zeile bleibt in Arial.",
        hint: "Mit Enter am Zeilenende legst du einen neuen Absatz an. Markiere danach nur diese Zeile.",
        check: async () => {
          const snapshot = await readDocument();

          if (snapshot.filledParagraphs.length < 2) {
            return fail(
              "Es gibt bisher nur einen Absatz mit Inhalt.",
              "Setze den Cursor ans Ende der Zeile, drücke Enter und schreibe eine zweite Zeile.",
            );
          }

          const arialParagraph = snapshot.filledParagraphs.find((paragraph) =>
            paragraph.runs.every((run) => isFont(run, "Arial")),
          );
          const timesParagraph = snapshot.filledParagraphs.find((paragraph) =>
            paragraph.runs.every((run) => isFont(run, "Times New Roman")),
          );

          return fromCriteria(
            [
              criterion("Ein Absatz ist vollständig in Arial gesetzt", Boolean(arialParagraph)),
              criterion(
                "Ein weiterer Absatz ist vollständig in Times New Roman gesetzt",
                Boolean(timesParagraph) && timesParagraph?.index !== arialParagraph?.index,
              ),
            ],
            {
              success: "Genau so sieht eine saubere Schriftkombination aus.",
              failure: `Das passt noch nicht ganz. Aktuell verwendest du: ${
                usedFontNames(snapshot).join(", ") || "keine erkennbare Schriftart"
              }.`,
              hint: "Markiere wirklich nur die zweite Zeile, bevor du Times New Roman zuweist.",
            },
          );
        },
      },
    },
  ],
};
